import * as Sentry from "@sentry/react";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  CampaignDeleteControl,
  CampaignTitleControl,
} from "@/components/dashboard/CampaignDetailViews";
import {
  MonolithBoardModal,
  MONOLITH_BOARD_MODAL_EYEBROW_CLASS,
  MONOLITH_BOARD_MODAL_TITLE_CLASS,
} from "@/components/dashboard/MonolithBoardModal";
import {
  getCampaignGradient,
  MONOLITH_BOARD_STYLE,
} from "@/components/dashboard/monolithBoardStyle";
import { Button } from "@/components/ui/Button";
import { useToast } from "@/components/ui/ToastProvider";
import { useEffectiveCampaignRole } from "@/hooks/useEffectiveCampaignRole";
import { cn } from "@/lib/classNames";
import { resolveDefaultCampaignShortId } from "@/lib/lastOpenedCampaign";
import {
  useCreateCampaignMutation,
  useDeleteCampaignMutation,
  useUpdateCampaignMutation,
  useUserCampaignsQuery,
} from "@/lib/queries/campaignQueries";
import { createInitialMap } from "@/state/createInitialMap";
import { useCampaignsModalStore } from "@/state/useCampaignsModalStore";
import {
  createMapDocument,
  loadTileCatalog,
  type UserCampaignSummary,
} from "@west-marches/shared";

// ~16:9 ocean rectangle that yields a hex count comparable to the previous
// radius=30 hexagon (~2,800 hexes). Used for authenticated new campaigns.
const DEFAULT_NEW_MAP_BOUNDS = { halfCols: 35, halfRows: 20 } as const;

const MODAL_TITLE_ID = "campaigns-modal-title";

function reportCampaignsModalError(
  action: string,
  error: unknown,
  extras?: Record<string, unknown>,
) {
  Sentry.captureException(error instanceof Error ? error : new Error(action), {
    tags: { area: "campaigns-modal", action },
    extra: extras,
  });
}

function sortCampaignsByUpdatedAt(campaigns: UserCampaignSummary[]): UserCampaignSummary[] {
  return [...campaigns].sort((left, right) => {
    const leftUpdated = Date.parse(left.updatedAt);
    const rightUpdated = Date.parse(right.updatedAt);
    if (Number.isNaN(leftUpdated) || Number.isNaN(rightUpdated)) {
      return left.id.localeCompare(right.id);
    }
    return rightUpdated - leftUpdated;
  });
}

function RoleBadge({ role }: { role: UserCampaignSummary["myRole"] }) {
  const label = role === "owner" ? "Owner" : role === "gm" ? "GM" : "Player";
  return (
    <span className="border-2 border-neutral-950 bg-[#fff2d9] px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.18em] text-neutral-950">
      {label}
    </span>
  );
}

function CampaignListRow({
  campaign,
  selected,
  active,
  onSelect,
  onOpen,
  busy,
}: {
  campaign: UserCampaignSummary;
  selected: boolean;
  active: boolean;
  onSelect: () => void;
  onOpen: () => void;
  busy: boolean;
}) {
  const gradient = getCampaignGradient(campaign.id);

  return (
    <li>
      <div
        className={cn(
          "group flex items-stretch gap-2 border-[3px] bg-[#fffaf1] transition-colors",
          selected
            ? "border-neutral-950 shadow-[6px_6px_0_rgba(19,19,19,0.16)]"
            : "border-neutral-950/30 hover:border-neutral-950 hover:bg-[#fff2d9]",
        )}
      >
        <button
          type="button"
          onClick={onSelect}
          aria-pressed={selected}
          aria-label={`Select campaign ${campaign.name}`}
          className="flex min-w-0 flex-1 items-start gap-3 px-3 py-3 text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#3e664d]"
        >
          <span
            aria-hidden="true"
            className="relative mt-0.5 flex h-11 w-11 shrink-0 overflow-hidden border-2 border-neutral-950"
            style={{ backgroundImage: gradient }}
          />

          <span className="flex min-w-0 flex-col gap-1.5">
            <span className="truncate text-[15px] font-black tracking-tight text-neutral-950">
              {campaign.name}
            </span>
            <span className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-neutral-600">
              <RoleBadge role={campaign.myRole} />
              <span>
                {campaign.roster.length} {campaign.roster.length === 1 ? "member" : "members"}
              </span>
              {active ? (
                <span
                  className="border border-[#3e664d]/30 bg-[#3e664d]/8 px-2 py-0.5 text-[9px] font-semibold tracking-[0.18em] text-[#2f4d39]"
                  data-testid="campaign-list-row-active-indicator"
                >
                  Current
                </span>
              ) : null}
            </span>
          </span>
        </button>
        <div className="flex shrink-0 items-center px-3 py-3">
          {active ? (
            <span
              className="inline-flex items-center border border-neutral-950/15 bg-neutral-950/5 px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.18em] text-neutral-600"
              aria-label={`${campaign.name} is currently open`}
            >
              Open now
            </span>
          ) : (
            <Button
              variant="primary"
              chrome="compact"
              type="button"
              disabled={busy}
              onClick={onOpen}
              className="px-3 py-2 text-[10px] tracking-[0.18em]"
              data-testid="campaign-list-row-open"
            >
              Open
            </Button>
          )}
        </div>
      </div>
    </li>
  );
}

function CampaignDetailPane({
  campaign,
  campaignsCount,
  onCampaignDeleted,
}: {
  campaign: UserCampaignSummary;
  campaignsCount: number;
  onCampaignDeleted: (deletedCampaign: UserCampaignSummary) => void;
}) {
  const toast = useToast();
  const updateCampaignMutation = useUpdateCampaignMutation();
  const deleteCampaignMutation = useDeleteCampaignMutation();

  const [renamingCampaign, setRenamingCampaign] = useState(false);
  const [deletingCampaign, setDeletingCampaign] = useState(false);

  const effective = useEffectiveCampaignRole(campaign);
  const canEdit = effective.effectiveCapabilities.canEditCampaign;
  const isLastCampaign = campaignsCount <= 1;
  const canDelete = effective.effectiveCapabilities.canDeleteCampaign && !isLastCampaign;

  const handleRenameCampaign = useCallback(
    async (name: string) => {
      if (!canEdit) return;
      setRenamingCampaign(true);
      try {
        const updated = await updateCampaignMutation.mutateAsync({
          campaignId: campaign.id,
          input: { name },
        });
        toast.success(`Renamed campaign to "${updated.name}".`);
      } catch (error) {
        reportCampaignsModalError("rename-campaign", error, {
          campaignId: campaign.id,
          nextName: name,
        });
        toast.error(error instanceof Error ? error.message : "Failed to rename campaign.");
      } finally {
        setRenamingCampaign(false);
      }
    },
    [campaign.id, canEdit, toast, updateCampaignMutation],
  );

  const handleDeleteCampaign = useCallback(async () => {
    if (!canDelete) return;
    setDeletingCampaign(true);
    try {
      await deleteCampaignMutation.mutateAsync(campaign.id);
      toast.success(`Deleted campaign "${campaign.name}".`);
      onCampaignDeleted(campaign);
    } catch (error) {
      reportCampaignsModalError("delete-campaign", error, { campaignId: campaign.id });
      toast.error(error instanceof Error ? error.message : "Failed to delete campaign.");
    } finally {
      setDeletingCampaign(false);
    }
  }, [campaign, canDelete, deleteCampaignMutation, onCampaignDeleted, toast]);

  return (
    <section
      aria-labelledby={`${MODAL_TITLE_ID}-detail-heading`}
      className={cn(
        "flex min-h-0 flex-1 flex-col overflow-hidden",
        MONOLITH_BOARD_STYLE.panelClassName,
        "shadow-[8px_8px_0_rgba(19,19,19,0.12)]",
      )}
    >
      <div className="border-b-2 border-neutral-950/15 p-5">
        <p className={MONOLITH_BOARD_MODAL_EYEBROW_CLASS} id={`${MODAL_TITLE_ID}-detail-heading`}>
          Campaign details
        </p>
        <div className="mt-3">
          <CampaignTitleControl
            campaign={campaign}
            canEdit={canEdit}
            onRenameCampaign={handleRenameCampaign}
            renamingCampaign={renamingCampaign}
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="p-5">
          {effective.effectiveCapabilities.canDeleteCampaign && isLastCampaign ? (
            <div className="space-y-2">
              <p className="text-[10px] uppercase tracking-[0.12em] text-neutral-600">Danger zone</p>
              <p className="text-[11px] leading-snug text-neutral-600">
                You can&apos;t delete your only campaign. Create another one first.
              </p>
            </div>
          ) : (
            <CampaignDeleteControl
              canDelete={canDelete}
              campaignName={campaign.name}
              onDeleteCampaign={() => {
                void handleDeleteCampaign();
              }}
              deletingCampaign={deletingCampaign}
            />
          )}
        </div>
      </div>
    </section>
  );
}

export default function CampaignsModal({
  activeCampaignShortId,
  userId,
}: {
  /** ShortId of the campaign currently being viewed in the editor (so we can mark/disable it). */
  activeCampaignShortId: string | null;
  /** Auth user id, used for last-opened persistence. */
  userId: string | null;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const open = useCampaignsModalStore((s) => s.open);
  const initialSelectedCampaignId = useCampaignsModalStore((s) => s.initialSelectedCampaignId);
  const closeModal = useCampaignsModalStore((s) => s.closeModal);

  const {
    data: campaigns = [],
    isPending,
    error: campaignsError,
    refetch: refetchCampaigns,
  } = useUserCampaignsQuery(undefined, Boolean(userId));

  // Re-fetch the campaigns list (which includes each campaign's roster) every
  // time the modal opens so newly joined or removed players show up without
  // requiring a page reload.
  useEffect(() => {
    if (!open || !userId) {
      return;
    }
    void refetchCampaigns();
  }, [open, userId, refetchCampaigns]);
  const createCampaignMutation = useCreateCampaignMutation();
  const [creatingCampaign, setCreatingCampaign] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);

  const sortedCampaigns = useMemo(() => sortCampaignsByUpdatedAt(campaigns), [campaigns]);

  const activeCampaign = useMemo(
    () => sortedCampaigns.find((c) => c.shortId === activeCampaignShortId) ?? null,
    [sortedCampaigns, activeCampaignShortId],
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    if (sortedCampaigns.length === 0) {
      setSelectedCampaignId(null);
      return;
    }
    setSelectedCampaignId((current) => {
      if (current && sortedCampaigns.some((c) => c.id === current)) {
        return current;
      }
      const seedId =
        initialSelectedCampaignId ?? activeCampaign?.id ?? sortedCampaigns[0]?.id ?? null;
      return seedId;
    });
  }, [open, initialSelectedCampaignId, activeCampaign?.id, sortedCampaigns]);

  useEffect(() => {
    if (!campaignsError) return;
    toast.error(campaignsError instanceof Error ? campaignsError.message : "Failed to load campaigns.");
  }, [campaignsError, toast]);

  const selectedCampaign = useMemo(
    () => sortedCampaigns.find((c) => c.id === selectedCampaignId) ?? null,
    [selectedCampaignId, sortedCampaigns],
  );

  const handleOpenCampaign = useCallback(
    (campaign: UserCampaignSummary) => {
      navigate({ to: "/campaign/$shortId", params: { shortId: campaign.shortId } });
      closeModal();
    },
    [closeModal, navigate],
  );

  const handleCreateCampaign = useCallback(async () => {
    const name = window.prompt("New campaign name", "New Campaign")?.trim();
    if (!name) {
      return;
    }

    setCreatingCampaign(true);
    try {
      await loadTileCatalog();
      const document = createMapDocument(createInitialMap(DEFAULT_NEW_MAP_BOUNDS));
      const campaign = await createCampaignMutation.mutateAsync({
        name,
        document,
      });
      navigate({ to: "/campaign/$shortId", params: { shortId: campaign.shortId } });
      closeModal();
    } catch (error) {
      reportCampaignsModalError("create-campaign", error, { name });
      toast.error(error instanceof Error ? error.message : "Failed to create campaign.");
    } finally {
      setCreatingCampaign(false);
    }
  }, [closeModal, createCampaignMutation, navigate, toast]);

  const handleCampaignDeleted = useCallback(
    (deletedCampaign: UserCampaignSummary) => {
      const remaining = sortedCampaigns.filter((c) => c.id !== deletedCampaign.id);
      const wasActive = activeCampaignShortId === deletedCampaign.shortId;

      if (wasActive) {
        const nextShortId = resolveDefaultCampaignShortId(remaining, userId);
        if (nextShortId) {
          navigate({ to: "/campaign/$shortId", params: { shortId: nextShortId }, replace: true });
        } else {
          navigate({ to: "/", replace: true });
          closeModal();
          return;
        }
      }

      const nextSelected = remaining[0];
      setSelectedCampaignId(nextSelected ? nextSelected.id : null);
    },
    [activeCampaignShortId, closeModal, navigate, sortedCampaigns, userId],
  );

  const submitting = creatingCampaign;

  return (
    <MonolithBoardModal
      open={open}
      titleId={MODAL_TITLE_ID}
      onClose={closeModal}
      submitting={submitting}
      panelClassName="max-w-[min(94vw,78rem)] sm:p-6"
    >
      <header className="flex items-start justify-between gap-4 border-b-2 border-neutral-950/15 pb-4">
        <div className="min-w-0">
          <p className={MONOLITH_BOARD_MODAL_EYEBROW_CLASS}>Workshop</p>
          <h2 id={MODAL_TITLE_ID} className={cn(MONOLITH_BOARD_MODAL_TITLE_CLASS, "mt-1")}>
            Campaigns
          </h2>
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          <Button
            type="button"
            variant="primary"
            chrome="frame"
            disabled={creatingCampaign}
            onClick={() => {
              void handleCreateCampaign();
            }}
            data-testid="campaigns-modal-new-campaign"
          >
            {creatingCampaign ? "Creating..." : "+ New campaign"}
          </Button>
          <Button
            type="button"
            variant="cancel"
            chrome="compact"
            onClick={closeModal}
            disabled={submitting}
            aria-label="Close campaigns modal"
            className="h-11"
          >
            Close
          </Button>
        </div>
      </header>

      <div className="mt-5 grid min-h-0 gap-5 md:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)] md:max-h-[70vh]">
        <aside
          aria-label="Campaign list"
          className={cn(
            "flex min-h-0 flex-col overflow-hidden",
            MONOLITH_BOARD_STYLE.panelClassName,
            "shadow-[8px_8px_0_rgba(19,19,19,0.12)]",
          )}
        >
          <div className="border-b-2 border-neutral-950/15 p-4">
            <p className={MONOLITH_BOARD_MODAL_EYEBROW_CLASS}>
              {sortedCampaigns.length} {sortedCampaigns.length === 1 ? "campaign" : "campaigns"}
            </p>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-3">
            {isPending ? (
              <p className="px-2 py-3 text-sm uppercase tracking-[0.2em] text-neutral-600">
                Loading campaigns...
              </p>
            ) : sortedCampaigns.length === 0 ? (
              <p className="px-2 py-3 text-sm leading-relaxed text-neutral-700">
                You don&apos;t have any campaigns yet. Click <strong>+ New campaign</strong> to start one.
              </p>
            ) : (
              <ul className="space-y-2" data-testid="campaigns-modal-list">
                {sortedCampaigns.map((campaign) => (
                  <CampaignListRow
                    key={campaign.id}
                    campaign={campaign}
                    selected={campaign.id === selectedCampaignId}
                    active={campaign.shortId === activeCampaignShortId}
                    onSelect={() => setSelectedCampaignId(campaign.id)}
                    onOpen={() => handleOpenCampaign(campaign)}
                    busy={submitting}
                  />
                ))}
              </ul>
            )}
          </div>
        </aside>

        {selectedCampaign ? (
          <CampaignDetailPane
            key={selectedCampaign.id}
            campaign={selectedCampaign}
            campaignsCount={sortedCampaigns.length}
            onCampaignDeleted={handleCampaignDeleted}
          />
        ) : (
          <section
            className={cn(
              "flex min-h-0 flex-1 items-center justify-center p-8 text-center text-sm text-neutral-700",
              MONOLITH_BOARD_STYLE.panelClassName,
              "shadow-[8px_8px_0_rgba(19,19,19,0.12)]",
            )}
          >
            <p>
              {sortedCampaigns.length === 0
                ? "Create your first campaign to get started."
                : "Select a campaign on the left to manage it."}
            </p>
          </section>
        )}
      </div>
    </MonolithBoardModal>
  );
}
